import type { SymDef } from './types';

/*
 * Manufacturer devices drawn as terminal blocks (EPLAN "device with
 * connection points"). A device may be split into several representations
 * that share the tag: the first is the main function, the others are
 * auxiliary functions with fixed terminal names.
 */

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;');
const P = (d: string, c?: string) => `<path d="${d}"${c ? ` class="${c}"` : ''}/>`;
const T = (x: number, y: number, s: number, t: string) => `<text x="${x}" y="${y}" font-size="${s}" text-anchor="middle">${esc(t)}</text>`;

/** A group of neighbouring terminals with a caption, e.g. [3, 8, 'JEN. AKIM L1-L3']. */
type Group = [first: number, last: number, caption: string];

interface BlockSpec {
  key: string;
  name: string;
  prefix: string;
  role: 'main' | 'aux';
  title: string;
  top: [number, number];
  bottom?: [number, number];
  topGroups: Group[];
  bottomGroups?: Group[];
  /** terminal names to use instead of numbers */
  names?: Record<number, string>;
}

const PITCH = 8, H = 40;

/** Terminal block: terminals at 8 mm pitch on the top and bottom edge, captions per group. */
function block(b: BlockSpec): SymDef {
  const n = Math.max(b.top[1] - b.top[0], b.bottom ? b.bottom[1] - b.bottom[0] : 0) + 1;
  const W = (n - 1) * PITCH, x0 = -6, x1 = W + 6, y0 = 4, y1 = y0 + H;
  const pins: [number, number, string][] = [];
  let svg = `<rect x="${x0}" y="${y0}" width="${x1 - x0}" height="${H}"/>`;
  const edge = (range: [number, number], groups: Group[], y: number, lead: number, capY: number) => {
    for (let t = range[0]; t <= range[1]; t++) {
      const x = (t - range[0]) * PITCH;
      pins.push([x, y, b.names?.[t] ?? String(t)]);
      svg += P(`M${x} ${y}V${lead}`);
    }
    for (const [f, l, cap] of groups) {
      const a = (f - range[0]) * PITCH, c = (l - range[0]) * PITCH;
      if (f !== range[0]) svg += P(`M${a - PITCH / 2} ${y === 0 ? y0 : y1 - 9}V${y === 0 ? y0 + 9 : y1}`);
      const words = cap.split('|');
      words.forEach((w, i) => (svg += T((a + c) / 2, capY + i * 2.3, 1.8, w)));
    }
  };
  edge(b.top, b.topGroups, 0, y0, y0 + 3.4);
  if (b.bottom) edge(b.bottom, b.bottomGroups ?? [], y1 + 4, y1, y1 - 6.6);
  svg += P(`M${x0} ${y0 + 9}H${x1}M${x0} ${y1 - 9}H${x1}`, 'dash');
  svg += T(W / 2, y0 + H / 2 + 1.6, 4.4, b.title);
  return { key: b.key, name: b.name, cat: 'Üretici cihazları', prefix: b.prefix, role: b.role, pins, bb: [x0, 0, x1, y1 + 4], svg };
}

export const VENDOR_DEFS: SymDef[] = [
  block({
    key: 'eg3200_ac', name: 'Woodward easYgen-3200XT · AC (1–40)', prefix: 'A', role: 'main', title: 'easYgen-3200XT  AC',
    top: [1, 20], bottom: [21, 40],
    topGroups: [
      [1, 2, 'ŞEB./GND|AKIM 1/5A'], [3, 8, 'JENERATÖR AKIM|L1 · L2 · L3 (1/5A)'], [9, 10, 'AI 01'], [11, 12, 'AI 02'], [13, 14, 'AI 03'],
      [15, 15, 'MOTOR|GND'], [16, 17, 'SPEED|AO 01'], [18, 18, 'NC'], [19, 20, 'AVR|AO 02'],
    ],
    bottomGroups: [[21, 28, 'ŞEBEKE GERİLİM|L1 · L2 · L3 · N'], [29, 36, 'JENERATÖR GERİLİM|L1 · L2 · L3 · N'], [37, 40, 'BARA GERİLİM|L1 · L2']],
  }),
  block({
    key: 'eg3200_dc', name: 'Woodward easYgen-3200XT · DC (41–80)', prefix: 'A', role: 'aux', title: 'easYgen-3200XT  DC',
    top: [41, 60], bottom: [61, 80],
    topGroups: [
      [41, 42, 'R01'], [43, 46, 'R02 · R03 · R04|ortak 46'], [47, 48, 'R05'], [49, 50, 'R06'], [51, 52, 'R07'], [53, 54, 'R08'],
      [55, 56, 'R09'], [57, 60, 'R10 · R11 · R12|ortak 60'],
    ],
    bottomGroups: [
      [61, 61, 'GND'], [62, 62, 'NC'], [63, 64, 'BESLEME|12/24V +  −'], [65, 65, 'D+'], [66, 66, 'DI|COM'],
      [67, 78, 'DİJİTAL GİRİŞLER DI01 … DI12'], [79, 80, 'MPU|+  −'],
    ],
  }),
  block({
    key: 'eg3200_com', name: 'Woodward easYgen-3200XT · CAN / RS485', prefix: 'A', role: 'aux', title: 'easYgen COM',
    top: [1, 3], bottom: [4, 5],
    names: { 1: 'CAN-L', 2: 'CAN-GND', 3: 'CAN-H', 4: 'RS485-A', 5: 'RS485-B' },
    topGroups: [[1, 3, 'CANBUS #1']],
    bottomGroups: [[4, 5, 'RS485']],
  }),
];
